import { useCallback, useEffect, useState } from "react";
import { Truck, CheckCircle, XCircle, CircleNotch, Warning, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { StockItemPicker } from "./StockItemPicker";
import { api, registerError, qty } from "./registerApi";

// Jun 26 2026, user's ask: "Add a tab STO Request with remark for the Goods
// movement, its move the claimed Material to the respective site HOLD
// warehouse. Any one can make this request but its approve by the Department
// Head" - their example: DEPT P1-CAST moves to P1-HOLD. The head's approval
// posts the SAP movement straight away, so it always asks first.

const STATUS = {
  pending: { label: "Waiting for the head", className: "text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]" },
  posting: { label: "Posting to SAP", className: "text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]" },
  approved: { label: "Moved in SAP", className: "text-[#027A48] bg-[#ECFDF3] border-[#A6F4C5]" },
  rejected: { label: "Rejected", className: "text-[#B42318] bg-[#FEF3F2] border-[#FDA29B]" },
  failed: { label: "SAP refused it", className: "text-[#B42318] bg-[#FEF3F2] border-[#FDA29B]" },
};

const RequestCard = ({ row, onDecided }) => {
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const status = STATUS[row.status] || STATUS.pending;

  const decide = async (kind) => {
    setBusy(kind);
    try {
      const result = kind === "approve" ? await api.approveStoRequest(row.id) : await api.rejectStoRequest(row.id, reason);
      if (kind === "approve") {
        result.status === "approved"
          ? toast.success(`Moved to ${row.target_area} · SAP document ${result.sap_gac_id}`)
          : toast.error(`SAP refused it: ${result.error}`);
      } else {
        toast.success(`${row.reference} rejected`);
      }
      setConfirming(false);
      setRejecting(false);
      onDecided();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border border-[#D0D5DD] rounded-sm bg-white" data-testid={`sto-request-${row.reference}`}>
      <div className="px-3 py-2 border-b border-[#EAECF0] flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-mono text-[13px] font-bold text-[#101828]">{row.reference}</span>
          <span className="text-[11px] text-[#667085] ml-2">
            {row.source_area} <span className="font-bold">&rarr;</span> {row.target_area}
          </span>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 shrink-0 ${status.className}`}
              data-testid={`sto-status-${row.reference}`}>
          {status.label}
        </span>
      </div>
      <div className="p-3 space-y-1.5">
        {row.lines.map((line) => (
          <div key={line.product_id} className="flex items-center justify-between gap-3 text-sm">
            <span className="font-mono text-[13px] font-bold text-[#101828] truncate">{line.product_id}</span>
            <span className="tabular-nums font-bold text-[#1D2939] shrink-0">
              {qty(line.quantity)} <span className="text-[11px] text-[#667085]">{line.unit_code}</span>
            </span>
          </div>
        ))}
        {row.remark && <p className="text-xs text-[#344054] bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-2 py-1.5">{row.remark}</p>}
        <p className="text-[11px] text-[#667085]">
          Asked by {row.requested_by}
          {row.decided_by ? ` · decided by ${row.decided_by}` : ""}
          {row.sap_gac_id ? ` · SAP document ${row.sap_gac_id}` : ""}
        </p>
        {row.error && (
          <p className="text-xs text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded-sm px-2 py-1.5" data-testid={`sto-error-${row.reference}`}>
            <Warning size={13} weight="bold" className="inline mr-1" />{row.error}
          </p>
        )}
        {row.rejection_reason && <p className="text-xs text-[#B42318]">Reason: {row.rejection_reason}</p>}

        {row.can_decide && !rejecting && (
          confirming ? (
            <div className="space-y-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-2" data-testid={`sto-confirm-${row.reference}`}>
              <p className="text-xs text-[#B54708]">
                This moves {row.lines.length} material{row.lines.length > 1 ? "s" : ""} out of{" "}
                <span className="font-mono font-bold">{row.source_area}</span> into{" "}
                <span className="font-mono font-bold">{row.target_area}</span> in SAP, right now. Approve?
              </p>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={busy === "approve"}
                        onClick={() => decide("approve")} data-testid={`sto-confirm-approve-${row.reference}`}>
                  {busy === "approve" ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="fill" />}
                  Yes, move it in SAP
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirming(false)} data-testid={`sto-confirm-cancel-${row.reference}`}>
                  Not yet
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="flex-1 h-10 bg-[#0E7C86] hover:bg-[#0B6B74]" onClick={() => setConfirming(true)}
                      data-testid={`sto-approve-${row.reference}`}>
                <CheckCircle size={14} weight="fill" /> {row.status === "failed" ? "Try again" : "Approve & move"}
              </Button>
              <Button size="sm" variant="outline" className="h-10" onClick={() => setRejecting(true)}
                      data-testid={`sto-reject-${row.reference}`}>
                <XCircle size={14} weight="bold" /> Reject
              </Button>
            </div>
          )
        )}
        {row.can_decide && rejecting && (
          <div className="space-y-2" data-testid={`sto-reject-form-${row.reference}`}>
            <Input className="h-10" placeholder="Why is it rejected?" value={reason}
                   onChange={(e) => setReason(e.target.value)} data-testid={`sto-reject-reason-${row.reference}`} />
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" className="flex-1" disabled={busy === "reject"}
                      onClick={() => decide("reject")} data-testid={`sto-reject-confirm-${row.reference}`}>
                {busy === "reject" ? <CircleNotch size={14} className="animate-spin" /> : null} Reject the request
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejecting(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const StoRequestPanel = ({ department }) => {
  const [rows, setRows] = useState([]);
  const [stock, setStock] = useState(null);
  const [lines, setLines] = useState([]);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const departmentId = department?.id;

  const load = useCallback(async () => {
    if (!departmentId) return;
    setLoading(true);
    try {
      const [requests, godown] = await Promise.all([api.stoRequests(departmentId), api.stoStock(departmentId)]);
      setRows(requests);
      setStock(godown);
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => { load(); }, [load]);

  const addLine = (item) => {
    if (lines.some((l) => l.product_id === item.product_id)) return;
    setLines((current) => [...current, { product_id: item.product_id, unit_code: item.uom, available: item.qty, quantity: "" }]);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const created = await api.createStoRequest({
        department_id: departmentId,
        lines: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity || 0) })),
        remark,
      });
      toast.success(`${created.reference} sent to ${department.name}'s head`);
      setLines([]);
      setRemark("");
      load();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const ready = lines.length > 0 && lines.every((l) => Number(l.quantity) > 0);

  return (
    <div className="space-y-3" data-testid="sto-request-panel">
      <div className="border border-[#D0D5DD] rounded-sm bg-white">
        <div className="px-3 py-2 border-b border-[#EAECF0] flex items-center gap-2">
          <Truck size={16} weight="bold" className="text-[#0E7C86]" />
          <span className="font-heading text-sm font-bold text-[#101828]">New STO request</span>
          <span className="text-[11px] text-[#667085] font-mono">
            {stock?.logistics_area_id || department?.logistics_area_id} &rarr; {department?.site_id}-HOLD
          </span>
        </div>
        <div className="p-3 space-y-2">
          <p className="text-xs text-[#667085]">
            Moves material standing in the department's own godown into the site's HOLD godown, ready for transfer.
            Anyone can ask; {department?.name}'s head approves, and SAP is updated the moment he does.
          </p>
          <StockItemPicker stock={stock} mappedProductIds={lines.map((l) => l.product_id)} onPick={addLine} busy={busy}
                           testIdPrefix="sto-item" />
          {lines.map((line, index) => (
            <div key={line.product_id} className="flex items-center gap-2 border border-[#D0D5DD] rounded-sm px-2.5 py-2"
                 data-testid={`sto-line-${line.product_id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[13px] font-bold text-[#101828] truncate">{line.product_id}</div>
                <div className="text-[11px] text-[#667085]">{qty(line.available)} {line.unit_code} in the godown</div>
              </div>
              <Input type="number" step="any" inputMode="decimal" className="w-24 h-10 text-right font-bold tabular-nums"
                     value={line.quantity} placeholder="0"
                     onChange={(e) => setLines((cur) => cur.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)))}
                     data-testid={`sto-qty-${line.product_id}`} />
              <Button size="icon" variant="ghost" className="h-10 w-9 text-[#B42318]"
                      onClick={() => setLines((cur) => cur.filter((_, i) => i !== index))}
                      data-testid={`sto-remove-${line.product_id}`}>
                <Trash size={15} weight="bold" />
              </Button>
            </div>
          ))}
          <Textarea rows={2} placeholder="Remark for the SAP document (why is it moving?)" value={remark}
                    onChange={(e) => setRemark(e.target.value)} data-testid="sto-remark" />
          <Button className="w-full h-11 bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={!ready || busy} onClick={submit}
                  data-testid="sto-submit">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : <Truck size={16} weight="bold" />} Send request
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center"><CircleNotch size={20} className="animate-spin text-[#0E7C86] mx-auto" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#667085] text-center py-6" data-testid="sto-none">No STO request yet for {department?.name}.</p>
      ) : (
        rows.map((row) => <RequestCard key={row.id} row={row} onDecided={load} />)
      )}
    </div>
  );
};
